import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import type { Addendum, NoteDetail, PatientDetails, UUID } from "@/core/types";
import { AddendumDisplay } from "@/features/addendum/components/AddendumDisplayRouter";
import { addendumRequestsTyped } from "@/api/(requests)/addendumTyped";
import { admin_cse } from "@/config/roles";
import { BackToPatientProfile } from "@/components/Common/BackToPatientProfile";
import { Button2 } from "@/ui2/atoms/Button";
import { Divider2 } from "@/ui2/atoms/Divider";
import { getQueryParams } from "@/utils/filters";
import { Heading2 } from "@/ui2/atoms/Heading";
import { isAuthorized } from "@/utils/authToken";
import PrintIcon from "@/assets/images/icons2/icon--print.svg?component";
import { sharedRequestsTyped } from "@/api/(requests)/sharedTyped";
import { showAlertWithAction } from "@/redux/actions/alerts";

import { formatSignatureDate } from "./helpers/formatSignatureDate";
import { isNoteCompleted } from "./helpers/isNoteCompleted";
import { NoteBadges } from "./components/NoteBadges";
import { NoteContentRouter } from "./components/NoteContentRouter";
import { SignatureList } from "./components/(signature)/SignatureList";

import "./components/BaseNoteLayout.print.styles.scss";

export function PrintNoteView() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { patientId } = useParams<{ patientId: UUID }>();
  const [notes, setNotes] = useState<NoteDetail[] | undefined>(undefined);
  const [user, setUser] = useState<PatientDetails | undefined>(undefined);
  const [addendums, setAddendums] = useState<Record<string, Addendum[]> | undefined>(undefined);

  const getPatient = async () => {
    try {
      const urlParams = { patientId };
      const patientResponse = await sharedRequestsTyped.fetch_patient_details({ urlParams });

      setUser(patientResponse.user);
    } catch (e) {
      console.log(e);
      dispatch(showAlertWithAction("Error fetching patient information."));
    }
  };

  const getNotes = async (noteIds: string[]) => {
    try {
      const urlParams = { patientId };
      const notes = isAuthorized(admin_cse)
        ? await sharedRequestsTyped.fetch_patient_progress_notes_batch({
            searchParams: { note_ids: noteIds as UUID[] },
            urlParams,
          })
        : [
            await sharedRequestsTyped.fetch_patient_progress_notes({
              searchParams: { note_id: noteIds[0] as UUID },
              urlParams,
            }),
          ];

      setNotes(
        notes.sort(
          (a, b) =>
            +moment(b.session_date + " " + (b.session_from ?? "00:00"), "MM/DD/YYYY HH:mm") -
            +moment(a.session_date + " " + (a.session_from ?? "00:00"), "MM/DD/YYYY HH:mm"),
        ),
      );
    } catch (e) {
      console.log(e);
      dispatch(showAlertWithAction("Error fetching notes to print."));
    }
  };

  const getNoteAddendums = async (noteIds: string[]) => {
    try {
      const urlParams = { patientId };
      const body = isAuthorized(admin_cse)
        ? { note_ids: noteIds as UUID[], patient_id: patientId }
        : { note_id: noteIds[0] as UUID, patient_id: patientId };

      const addendumResponse = await addendumRequestsTyped.get_note_addendums({
        body,
        urlParams,
      });

      const newAddendums = addendumResponse.addendums.reduce<Record<string, Addendum[]>>(
        (obj, addendum) => {
          const noteId = addendum.note_id;

          if (obj[noteId] === undefined) {
            obj[noteId] = [];
          }

          obj[noteId].push(addendum);

          return obj;
        },
        {},
      );

      setAddendums(newAddendums);
    } catch (e) {
      console.log(e);
      dispatch(showAlertWithAction("Error fetching note addendums."));
    }
  };

  const printoutHeader = () => (
    <header className="tw:flex tw:justify-between">
      <div>
        <div className="fs-22 tw:font-bold" data-private>
          {user!.first_name} {user!.last_name}
        </div>

        <div>Date of Birth: {user!.dob}</div>
      </div>

      <div className="text-right">
        <div>{notes!.length} Notes</div>
        <div>Exported on: {moment().local().format("DD/MM/YYYY")}</div>
      </div>
    </header>
  );

  const noteHeader = (note: NoteDetail) => {
    if (note.note_type === "Care Management" || note.note_type === "Advanced Care Activity") {
      return null;
    }

    return (
      <header className="tw:pb-5">
        <p className="tw:font-bold">{note.title || `${note.note_type} ${note.session_date}`}</p>
      </header>
    );
  };

  useEffect(() => {
    (async () => {
      try {
        let noteIds = getQueryParams("noteIds", location.search.trim());

        if (!noteIds) return;

        let noteIdList = noteIds.split(",");

        if (noteIdList.length > 50) {
          dispatch(
            showAlertWithAction(
              "Too many notes selected for export. Please reduce the selection and try again (max: 50).",
            ),
          );

          return;
        }

        await getPatient();

        await getNotes(noteIdList);

        await getNoteAddendums(noteIdList);
      } catch (e) {
        console.log(e);
      }
    })();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- route params won't change while mounted
  }, []);

  useEffect(() => {
    if (user !== undefined && notes !== undefined && addendums !== undefined) {
      document.title = `${user.first_name} ${user.last_name} ${notes.length} Notes -
            ${moment().local().format("YYYY-MM-DD HH:mm")}`;
      window.print();
    }
  }, [user, notes, addendums]);

  return (
    <>
      <nav className="tw:mb-5 tw:flex tw:justify-between tw:print:hidden">
        <BackToPatientProfile patientId={patientId} />

        <Button2
          className="tw:text-text-tertiary"
          onPress={() => window.print()}
          size="sm"
          variant="link"
        >
          <PrintIcon className="tw:mr-ch" />
          Print
        </Button2>
      </nav>

      <div className="tw:not-print:bg-white">
        {user !== undefined && notes !== undefined && printoutHeader()}

        <ul className="tw:flex tw:flex-col tw:gap-10">
          {user !== undefined &&
            addendums !== undefined &&
            notes !== undefined &&
            notes.map((note, index) => {
              const patientName = `${user.first_name} ${user.last_name}`;

              return (
                <li
                  className="tw:flex tw:flex-col tw:not-only:not-last:break-after-page"
                  key={note.id || index}
                >
                  <NoteBadges className="tw:mt-2.5 tw:mr-auto tw:list-none" note={note} />
                  <Divider2 className="tw:mt-5! tw:mb-10! tw:bg-fill-black" />

                  {noteHeader(note)}

                  <NoteContentRouter note={note} patientName={patientName} />

                  <SignatureList
                    cosignedBy={isNoteCompleted(note.note_state) ? note.cosigned_by : undefined}
                    credentials={note.provider_cp_credentials}
                    isCosignRequired={note.is_cosign_required}
                    isNoteComplete={isNoteCompleted(note.note_state)}
                    providerPractice={note.provider_practice}
                    signature={note.signature}
                    signatureDate={formatSignatureDate(note)}
                  />

                  {addendums[note.id] && (
                    <div className="tw:mt-10">
                      <Heading2 size={2}>Addendums</Heading2>
                      <Divider2 className="tw:mt-2.5! tw:mb-5! tw:bg-fill-black" />

                      <ul className="tw:flex tw:list-disc tw:flex-col tw:gap-3">
                        {addendums[note.id].map((addendum, addendumIndex) => (
                          <li key={addendum.addendum_id ?? addendumIndex}>
                            <AddendumDisplay
                              addendum={addendum}
                              key={addendum.addendum_id ?? addendumIndex}
                              noteType={note.note_type}
                              style={{ marginBottom: 10, minHeight: 0 }}
                              textTestId={undefined}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              );
            })}
        </ul>
      </div>
    </>
  );
}
